'use client'

import { useId, useState } from 'react'
import Image from 'next/image'
import { Link } from '@/components/site/link'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import { coverImage, postPath } from '@/lib/site'
import type { PostSummary } from '@/types/site'

export function PostGrid({
  posts,
  compact = false,
}: {
  posts: PostSummary[]
  compact?: boolean
}) {
  const t = useTranslations('postGrid')
  const [count, setCount] = useState(6)
  const id = useId()
  const shown = Math.min(compact ? posts.length : count, posts.length)
  return (
    <div className={compact ? 'site-post-list compact' : 'site-post-list'}>
      <div id={id} className={compact ? 'site-post-grid compact' : 'site-post-grid'}>
        {/* Every card is in the HTML so crawlers reach every post; View More only reveals them. */}
        {posts.map((post, index) => (
          <article key={`${post.locale}/${post.slug}`} className="site-post-card" hidden={!compact && index >= count}>
            <Link href={postPath(post)}>
              {coverImage(post) ? (
                <Image
                  src={coverImage(post)}
                  alt={typeof post.coverImage === 'object' ? post.coverImage.alt : post.title}
                  width={640}
                  height={420}
                  sizes={
                    compact ? '(max-width: 809px) 100vw, 200px' : '(max-width: 809px) 100vw, 33vw'
                  }
                />
              ) : null}
              <div className="site-post-card-content">
                <h2>{post.title}</h2>
                <p>{post.excerpt}</p>
                {compact ? (
                  <div className="site-post-categories">
                    {post.categories?.map((category) => (
                      <span key={category.id || category.name}>{category.name}</span>
                    ))}
                  </div>
                ) : (
                  <span className="site-post-read-more">
                    {t('readFullArticle')} →
                  </span>
                )}
              </div>
            </Link>
          </article>
        ))}
      </div>
      {!compact && count < posts.length ? (
        <Button
          className="site-post-load-more"
          aria-controls={id}
          onClick={() => setCount((value) => value + 6)}
        >
          {t('viewMore')}
        </Button>
      ) : null}
      <span className="sr-only" role="status">
        {t('articlesCount', { current: shown, total: posts.length })}
      </span>
      {!posts.length ? (
        <p>{t('noArticles')}</p>
      ) : null}
    </div>
  )
}
